import { useState, type FormEvent } from "react";

import type { Firm, FirmInput, FirmPatch } from "../lib/api";

/**
 * Used for both create and edit. The `initial` prop, when provided, seeds the
 * fields and locks the name (we don't support renames — see ADR-0004).
 *
 * - In create mode (`initial` undefined): submits a full FirmInput.
 * - In edit mode: submits a FirmPatch (name omitted; server identifies by URL).
 */
export default function FirmForm({
  title,
  initial,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Firm;
  onSubmit: (value: FirmInput | FirmPatch) => Promise<void>;
  onCancel: () => void;
}) {
  const isEdit = initial !== undefined;

  const [name, setName] = useState(initial?.name ?? "");
  const [website, setWebsite] = useState(initial?.website ?? "");
  const [supportsTradelocker, setSupportsTradelocker] = useState<boolean>(
    initial?.supports_tradelocker ?? false,
  );
  const [active, setActive] = useState<boolean>(initial?.active ?? true);
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // URL sanity check — matches the API's regex.
    if (website.trim() && !/^https?:\/\//.test(website.trim())) {
      setError("Website must start with http:// or https://");
      return;
    }

    setBusy(true);
    try {
      if (isEdit) {
        const patch: FirmPatch = {
          website: website.trim(),
          supports_tradelocker: supportsTradelocker,
          active,
          notes: notes.trim(),
        };
        await onSubmit(patch);
      } else {
        if (!name.trim()) {
          setError("Name is required");
          setBusy(false);
          return;
        }
        const input: FirmInput = {
          name: name.trim(),
          website: website.trim(),
          supports_tradelocker: supportsTradelocker,
          active,
          notes: notes.trim(),
        };
        await onSubmit(input);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <h2>{title}</h2>

      <label>
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isEdit}
          required={!isEdit}
          placeholder="e.g., Apex Trader Funding"
        />
        {isEdit && (
          <small>
            Name is the primary key and can't be renamed. Deactivate this firm
            and create a new one if you need to rename.
          </small>
        )}
      </label>

      <label>
        Website
        <input
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://example.com"
        />
      </label>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={supportsTradelocker}
          onChange={(e) => setSupportsTradelocker(e.target.checked)}
        />
        Supports TradeLocker
      </label>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        Active (uncheck to hide from new-account dropdown without losing data)
      </label>

      <label>
        Notes
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything else worth remembering about this firm."
        />
      </label>

      {error && <div className="error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
